import React, { useState, useEffect } from "react";
import AdminTopNavBar from "../common/AdminTopNavBar";
import "../../styles/Admin.css";
import AdminFooter from "../common/AdminFooter";
import AdminSideBar from "./AdminSideBar";
import getUserMenuItems from "./AdminMenuContent";
import { api, adminReqConfig } from "../../include/api";
import { useNotification } from "../../Toast/ToastProvider";
import "../../styles/CategoryProductList.css";
import { useNavigate } from "react-router-dom";

const AddRole = () => {
  const homeLink = "/admin";
  const [isSidebarMinimized, setIsSidebarMinimized] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [roleName, setRoleName] = useState("");
  const [status, setStatus] = useState(true);
  const [fetchingRole, setFetchingRole] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const toast = useNotification();
  const urlParams = new URLSearchParams(window.location.search);
  const roleIdFromUrl = urlParams.get("id");
  const isViewMode = urlParams.get("view") === "true";
  const isEditMode = !!roleIdFromUrl && !isViewMode;
  const [roleId, setRoleId] = useState(
    roleIdFromUrl ? parseInt(roleIdFromUrl, 10) : null,
  );
  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedRoleForPermissions, setSelectedRoleForPermissions] =
    useState("");
  const [rolesWithPermissions, setRolesWithPermissions] = useState(new Set());

  // AddRole.jsx - Add this state near other useState hooks
  const [existingPermissionUrls, setExistingPermissionUrls] = useState(
    new Set(),
  );
  const [hasExistingPermission, setHasExistingPermission] = useState(false);

  // BUTTON STYLING HELPERS
  const getButtonStyle = (isPrimary = false, isSmall = false) => ({
    border: "none",
    padding: isSmall ? "8px 12px" : "8px 20px",
    fontWeight: "500",
    borderRadius: "5px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: saving ? "not-allowed" : "pointer",
    opacity: saving ? 0.7 : 1,
    fontSize: isSmall ? "14px" : "inherit",
    minWidth: isSmall ? "80px" : "auto",
    textAlign: "center",
    ...(isPrimary
      ? {
          backgroundColor: saving ? "#1565c0" : "#1976d2",
          color: "#fff",
        }
      : {
          backgroundColor: saving ? "#e0e0e0" : "#bed9f8ff",
          color: saving ? "#9e9e9e" : "#1976d2",
        }),
  });

  const renderLoadingSpinner = (size = "sm") => (
    <i
      className={`fa fa-spinner fa-spin ${size === "sm" ? "me-2" : "me-3"}`}
      aria-hidden="true"
      style={{ fontSize: size === "sm" ? "0.9rem" : "1.1rem" }}
    ></i>
  );

  // --- Permission State ---
  const [permissions, setPermissions] = useState({
    Category: { add: false, view: false, edit: false, delete: false },
    Brands: { add: false, view: false, edit: false, delete: false },
    Products: { add: false, view: false, edit: false, delete: false },
    "Sales Orders": { add: false, view: false, edit: false, delete: false },
    "Sales Invoices": { add: false, view: false, edit: false, delete: false },
    "Sales Payments": { add: false, view: false, edit: false, delete: false },
    "Payment Proof": { add: false, view: false, edit: false, delete: false },
    Content: { add: false, view: false, edit: false, delete: false },
    "Delivery Charge": { add: false, view: false, edit: false, delete: false },
    Pincode: { add: false, view: false, edit: false, delete: false },
    Roles: { add: false, view: false, edit: false, delete: false },
    Users: { add: false, view: false, edit: false, delete: false },
    Customers: { add: false, view: false, edit: false, delete: false },
    Coupon: { add: false, view: false, edit: false, delete: false },
    "Document Verify": { add: false, view: false, edit: false, delete: false },
    "Report Sales": { add: false, view: false, edit: false, delete: false },
    "Report Products": { add: false, view: false, edit: false, delete: false },
    "Report Customer": { add: false, view: false, edit: false, delete: false },
    Notification: { add: false, view: false, edit: false, delete: false },
    "Loyality Points": { add: false, view: false, edit: false, delete: false },
    Settings: { add: false, view: false, edit: false, delete: false },
    Logs: { add: false, view: false, edit: false, delete: false },
    "Audit Log": { add: false, view: false, edit: false, delete: false },
  });

  // --- Mapping Helpers ---
  const DB_PAGE_TITLE_MAP = {
    Category: "Category",
    Brands: "Brands",
    Products: "Products",
    "Sales Orders": "Sales Orders",
    "Sales Invoices": "Sales Invoices",
    "Sales Payments": "Sales Payments",
    "Payment Proof": "Payment Proof",
    Content: "Content",
    "Delivery Charge": "Delivery Charge",
    Pincode: "Pincode",
    Roles: "Roles",
    Users: "Users",
    Customers: "Customers",
    Coupon: "Coupon",
    "Document Verify": "Document Verify",
    "Report Sales": "Report Sales",
    "Report Products": "Report Products",
    "Report Customer": "Report Customer",
    Notification: "Notification",
    "Loyality Points": "Loyality Points",
    Settings: "Settings",
    Logs: "Logs",
    "Audit Log": "Audit Log",
  };

  const getPageUrlFromModuleKey = (moduleKey) => {
    const urlMap = {
      Category: "/admin/category/categoryList",
      Brands: "/admin/brand/brandList",
      Products: "/admin/product/productList",
      "Sales Orders": "/admin/sales/orders",
      "Sales Invoices": "/admin/sales/invoice",
      "Sales Payments": "/admin/sales/payment",
      "Payment Proof": "/admin/sales/paymentProof",
      Content: "/admin/content/contentList",
      "Delivery Charge": "/admin/deliveryCharge/deliveryChargeList",
      Pincode: "/admin/pincode/pincodeList",
      Roles: "/admin/role/roleList",
      Users: "/admin/user/userList",
      Customers: "/admin/customer/customerList",
      Coupon: "/admin/coupon/couponList",
      "Document Verify": "/admin/document/documentVerify",
      "Report Sales": "/admin/reports/reportSales",
      "Report Products": "/admin/reports/reportProducts",
      "Report Customer": "/admin/reports/reportCustomer",
      Notification: "/admin/notification/notificationList",
      "Loyality Points": "/admin/loyalityPoints",
      Settings: "/admin/settings",
      Logs: "/admin/logs",
      "Audit Log": "/admin/audit-log",
    };
    return urlMap[moduleKey] || "/";
  };

  const getPageTitleFromModuleKey = (moduleKey) => {
    return DB_PAGE_TITLE_MAP[moduleKey] || moduleKey;
  };

  const fetchRolesWithPermissions = async () => {
    try {
      const res = await api.get("/role_permission/", adminReqConfig());
      if (res.status === 200 && Array.isArray(res.data.data)) {
        const roleIds = new Set(
          res.data.data.map((item) => item.roleId).filter((id) => id != null),
        );
        setRolesWithPermissions(roleIds);
      }
    } catch (err) {
      console.warn("Could not fetch roles with permissions:", err);
    }
  };
  const toggleSidebar = () => setIsSidebarMinimized(!isSidebarMinimized);
  const menuContent = getUserMenuItems();

  // --- Fetch all roles for dropdown ---
  const fetchAvailableRoles = () => {
    api
      .get("/role/", adminReqConfig())
      .then((response) => {
        if (response.status === 200) {
          const roles = response.data.data || [];
          setAvailableRoles(roles);
        }
      })
      .catch(() => {
        toast({ type: "error", message: "Failed to load roles." });
      });
  };

  // --- Fetch only role details (no permissions) ---
  const fetchRoleDetails = (id) => {
    setFetchingRole(true);
    api
      .get(`/role/?id=${id}`, adminReqConfig())
      .then((res) => {
        if (res.status === 200) {
          const role = Array.isArray(res.data.data)
            ? res.data.data[0]
            : res.data.data;
          if (role) {
            setRoleName(role.roleName || "");
            setStatus(role.status === "active");
          }
        }
      })
      .catch(() => {
        toast({ type: "error", message: "Failed to load role details." });
      })
      .finally(() => setFetchingRole(false));
  };

  useEffect(() => {
    fetchAvailableRoles();
    if (!isEditMode && !isViewMode) {
      fetchRolesWithPermissions();
    }

    if (roleId) {
      fetchRoleDetails(roleId);
      setSelectedRoleForPermissions(String(roleId));
      loadPermissionsForRole(roleId);
    } else {
      setFetchingRole(false);
    }
  }, [roleId, isEditMode, isViewMode]);

  const validate = () => {
    if (!roleName.trim()) {
      toast({ type: "error", message: "Role name is required" });
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const rolePayload = {
        roleName: roleName.trim(),
        status: status ? "active" : "inactive",
      };
      let roleResponse;
      if (!isEditMode) {
        roleResponse = await api.post("/role/", rolePayload, adminReqConfig());
      } else {
        roleResponse = await api.put(
          `/role/?id=${roleId}`,
          rolePayload,
          adminReqConfig(),
        );
      }
      if (![200, 201].includes(roleResponse.status))
        throw new Error("Role save failed");
      toast({
        type: "success",
        message: isEditMode
          ? "Role updated successfully!"
          : "Role added successfully!",
      });
      navigate("/admin/role/roleList");
      if (!isEditMode) clearFields();
    } catch (err) {
      console.error("Save error:", err);
      const msg =
        err.response?.data?.message ||
        (isEditMode ? "Failed to update role" : "Failed to add role");
      toast({ type: "error", message: msg });
    } finally {
      setSaving(false);
    }
  };

  // const handleSavePermissions = async () => {
  //   if (!selectedRoleForPermissions) {
  //     toast({
  //       type: "error",
  //       message: "Please select a role to manage permissions.",
  //     });
  //     return;
  //   }

  //   setSaving(true);
  //   try {
  //     const roleIdNum = parseInt(selectedRoleForPermissions, 10);

  //     const roleProperties = Object.keys(permissions).map((moduleKey) => ({
  //       shopId: 1,
  //       pageTitle: getPageTitleFromModuleKey(moduleKey),
  //       pageUrl: getPageUrlFromModuleKey(moduleKey),
  //       add: permissions[moduleKey].add ? 1 : 0,
  //       view: permissions[moduleKey].view ? 1 : 0,
  //       edit: permissions[moduleKey].edit ? 1 : 0,
  //       delete: permissions[moduleKey].delete ? 1 : 0,
  //     }));

  //     const payload = {
  //       roleId: [roleIdNum],
  //       roleProperties,
  //     };

  //     let response;

  //     if (hasExistingPermission) {
  //       response = await api.put(
  //         "/role_permission/",
  //         payload,
  //         adminReqConfig(),
  //       );
  //     } else {
  //       response = await api.post(
  //         "/role_permission/",
  //         payload,
  //         adminReqConfig(),
  //       );
  //     }

  //     if ([200, 201].includes(response.status)) {
  //       toast({ type: "success", message: "Permissions saved successfully!" });
  //       navigate("/admin/role/roleList");
  //     } else {
  //       throw new Error("Unexpected response status");
  //     }
  //   } catch (err) {
  //     console.error("Permission save error:", err);
  //     const msg = err.response?.data?.message || "Failed to save permissions.";
  //     toast({ type: "error", message: msg });
  //   } finally {
  //     setSaving(false);
  //   }
  // };

  // const loadPermissionsForRole = async (roleId) => {
  //   if (!roleId) {
  //     resetPermissions();
  //     setHasExistingPermission(false);
  //     return;
  //   }

  //   try {
  //     const res = await api.get(
  //       `/role_permission/?roleId=${roleId}`,
  //       adminReqConfig(),
  //     );

  //     if (
  //       res.status === 200 &&
  //       Array.isArray(res.data.data) &&
  //       res.data.data.length > 0
  //     ) {
  //       // Permissions exist
  //       setHasExistingPermission(true);

  //       const permMap = {};
  //       res.data.data.forEach((item) => {
  //         permMap[item.pageTitle] = {
  //           add: item.add === "true" || item.add === 1 || item.add === "1",
  //           view: item.view === "true" || item.view === 1 || item.view === "1",
  //           edit: item.edit === "true" || item.edit === 1 || item.edit === "1",
  //           delete:
  //             item.delete === "true" ||
  //             item.delete === 1 ||
  //             item.delete === "1",
  //         };
  //       });

  //       const newPermissions = { ...permissions };
  //       Object.keys(newPermissions).forEach((moduleKey) => {
  //         const pageTitle = getPageTitleFromModuleKey(moduleKey);
  //         newPermissions[moduleKey] = permMap[pageTitle] || {
  //           add: false,
  //           view: false,
  //           edit: false,
  //           delete: false,
  //         };
  //       });

  //       setPermissions(newPermissions);
  //     } else {
  //       // No permissions found
  //       setHasExistingPermission(false);
  //       resetPermissions();
  //     }
  //   } catch (err) {
  //     console.error("Permission load error:", err);
  //     setHasExistingPermission(false);
  //     resetPermissions();
  //   }
  // };

const handleSavePermissions = async () => {
  if (!selectedRoleForPermissions) {
    toast({
      type: "error",
      message: "Please select a role to manage permissions.",
    });
    return;
  }

  setSaving(true);
  try {
    const roleIdNum = parseInt(selectedRoleForPermissions, 10);

    // Build full permission list
    const allPermissions = Object.keys(permissions).map((moduleKey) => ({
      shopId: 1,
      pageTitle: getPageTitleFromModuleKey(moduleKey),
      pageUrl: getPageUrlFromModuleKey(moduleKey),
      add: permissions[moduleKey].add ? 1 : 0,
      view: permissions[moduleKey].view ? 1 : 0,
      edit: permissions[moduleKey].edit ? 1 : 0,
      delete: permissions[moduleKey].delete ? 1 : 0,
    }));

    // ✅ Split into NEW (not in DB) vs EXISTING (already in DB)
    const newPermissions = allPermissions.filter(
      (p) => !existingPermissionUrls.has(p.pageUrl),
    );
    const existingPermissions = allPermissions.filter((p) =>
      existingPermissionUrls.has(p.pageUrl),
    );

    const results = [];

    // ✅ POST new permissions (insert)
    if (newPermissions.length > 0) {
      const postRes = await api.post(
        "/role_permission/",
        { roleId: [roleIdNum], roleProperties: newPermissions },
        adminReqConfig(),
      );
      results.push(postRes);
    }

    // ✅ PUT existing permissions (update)
    if (existingPermissions.length > 0) {
      const putRes = await api.put(
        "/role_permission/",
        { roleId: [roleIdNum], roleProperties: existingPermissions },
        adminReqConfig(),
      );
      results.push(putRes);
    }

    // ✅ Check all responses succeeded
    const allSuccess = results.every((r) => [200, 201].includes(r?.status));

    if (allSuccess) {
      toast({ type: "success", message: "Permissions saved successfully!" });
      navigate("/admin/role/roleList");
    } else {
      throw new Error("One or more permission operations failed");
    }
  } catch (err) {
    console.error("Permission save error:", err);
    const msg = err.response?.data?.message || "Failed to save permissions.";
    toast({ type: "error", message: msg });
  } finally {
    setSaving(false);
  }
};
  const loadPermissionsForRole = async (roleId) => {
  if (!roleId) {
    resetPermissions();
    setHasExistingPermission(false);
    setExistingPermissionUrls(new Set()); // ✅ Clear when no role
    return;
  }

  try {
    const res = await api.get(
      `/role_permission/?roleId=${roleId}`,
      adminReqConfig(),
    );

    if (
      res.status === 200 &&
      Array.isArray(res.data.data) &&
      res.data.data.length > 0
    ) {
      setHasExistingPermission(true);

      // ✅ Extract existing pageUrls into a Set for quick lookup
      const urlSet = new Set(res.data.data.map(item => item.pageUrl));
      setExistingPermissionUrls(urlSet);

      const permMap = {};
      res.data.data.forEach((item) => {
        permMap[item.pageTitle] = {
          add: item.add === "true" || item.add === 1 || item.add === "1",
          view: item.view === "true" || item.view === 1 || item.view === "1",
          edit: item.edit === "true" || item.edit === 1 || item.edit === "1",
          delete: item.delete === "true" || item.delete === 1 || item.delete === "1",
        };
      });

      const newPermissions = { ...permissions };
      Object.keys(newPermissions).forEach((moduleKey) => {
        const pageTitle = getPageTitleFromModuleKey(moduleKey);
        newPermissions[moduleKey] = permMap[pageTitle] || {
          add: false, view: false, edit: false, delete: false,
        };
      });

      setPermissions(newPermissions);
    } else {
      setHasExistingPermission(false);
      setExistingPermissionUrls(new Set());
      resetPermissions();
    }
  } catch (err) {
    console.error("Permission load error:", err);
    setHasExistingPermission(false);
    setExistingPermissionUrls(new Set());
    resetPermissions();
  }
  };
  

  const resetPermissions = () => {
    if (saving) return;
    setPermissions({
      Category: { add: false, view: false, edit: false, delete: false },
      Brands: { add: false, view: false, edit: false, delete: false },
      Products: { add: false, view: false, edit: false, delete: false },
      "Sales Orders": { add: false, view: false, edit: false, delete: false },
      "Sales Invoices": { add: false, view: false, edit: false, delete: false },
      "Sales Payments": { add: false, view: false, edit: false, delete: false },
      "Payment Proof": { add: false, view: false, edit: false, delete: false },
      Content: { add: false, view: false, edit: false, delete: false },
      "Delivery Charge": {
        add: false,
        view: false,
        edit: false,
        delete: false,
      },
      Pincode: { add: false, view: false, edit: false, delete: false },
      Roles: { add: false, view: false, edit: false, delete: false },
      Users: { add: false, view: false, edit: false, delete: false },
      Customers: { add: false, view: false, edit: false, delete: false },
      Coupon: { add: false, view: false, edit: false, delete: false },
      "Document Verify": {
        add: false,
        view: false,
        edit: false,
        delete: false,
      },
      "Report Sales": { add: false, view: false, edit: false, delete: false },
      "Report Products": {
        add: false,
        view: false,
        edit: false,
        delete: false,
      },
      "Report Customer": {
        add: false,
        view: false,
        edit: false,
        delete: false,
      },
      Notification: { add: false, view: false, edit: false, delete: false },
      "Loyality Points": {
        add: false,
        view: false,
        edit: false,
        delete: false,
      },
      Settings: { add: false, view: false, edit: false, delete: false },
      Logs: { add: false, view: false, edit: false, delete: false },
      "Audit Log": { add: false, view: false, edit: false, delete: false },
    });
  };

  const clearFields = () => {
    if (saving) return;
    setRoleName("");
    setStatus(true);
  };

  const clearFieldsPermission = () => {
    if (saving || isViewMode) return;
    resetPermissions();
  };

  const handlePermissionChange = (module, action) => {
    if (isViewMode || saving) return;
    setPermissions((prev) => ({
      ...prev,
      [module]: {
        ...prev[module],
        [action]: !prev[module][action],
      },
    }));
  };

  const inputStyle = {
    borderRadius: "5px",
    fontWeight: "400",
    outline: "none",
    width: "100%",
    padding: "11px 24px",
    backgroundColor: "transparent !important",
    border: "1px solid var(--gray-100)",
    color: "hsl(var(--black))",
    opacity: saving ? 0.7 : 1,
    pointerEvents: saving ? "none" : "auto",
  };

  const permissionTableHeaderStyle = {
    backgroundColor: "#D9EBFF",
    padding: "12px 16px",
    borderBottom: "1px solid #e2e8f0",
    fontWeight: "600",
    fontSize: "14px",
    color: "#2F97D4",
  };

  const permissionTableRowStyle = {
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
    gap: "16px",
  };

  const permissionCheckboxContainerStyle = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };

  if (fetchingRole) {
    return <div className="text-center mt-5">Loading role details...</div>;
  }

  return (
    <>
      {!isViewMode && showRoleModal && (
        <div
          className="modal show"
          style={{
            display: "block",
            backgroundColor: "rgba(0,0,0,0.5)",
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 1050,
          }}
          onClick={() => !saving && setShowRoleModal(false)}
        >
          <div
            className="modal-lg"
            style={{ maxWidth: "800px", margin: "60px auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="pt-22 px-16">
                <h6 className="mb-0 fs-5">
                  {isEditMode ? "Edit Role" : "Add Role"}
                </h6>
                <button
                  onClick={() => !saving && setShowRoleModal(false)}
                  disabled={saving}
                  style={{
                    position: "absolute",
                    top: "12px",
                    right: "12px",
                    width: "32px",
                    height: "32px",
                    background: "none",
                    border: "3px solid #ddd",
                    borderRadius: "50%",
                    fontSize: "24px",
                    cursor: saving ? "not-allowed" : "pointer",
                    color: saving ? "#ccc" : "#6E7C94",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: 0,
                    opacity: saving ? 0.5 : 1,
                  }}
                >
                  &times;
                </button>
              </div>
              <hr />
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <h6 className="text-md fw-bold">Role Name</h6>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter Role Name"
                      value={roleName}
                      onChange={(e) => !saving && setRoleName(e.target.value)}
                      disabled={saving}
                      style={inputStyle}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <h6 className="text-md fw-bold">Select Status</h6>
                    <div className="d-flex gap-3 align-items-center pt-1">
                      {[
                        { label: "Active", value: true },
                        { label: "Inactive", value: false },
                      ].map((option) => (
                        <div
                          key={option.label}
                          className="form-check d-flex align-items-center"
                          style={{ margin: 0 }}
                        >
                          <input
                            type="radio"
                            className="form-check-input me-5"
                            id={`status-${option.label}`}
                            checked={status === option.value}
                            onChange={() => !saving && setStatus(option.value)}
                            disabled={saving}
                            style={{
                              cursor: saving ? "not-allowed" : "pointer",
                            }}
                          />
                          <h6
                            className="text-md fw-bold mb-0 ms-2 me-16"
                            htmlFor={`status-${option.label}`}
                            style={{
                              opacity: saving ? 0.7 : 1,
                              cursor: saving ? "not-allowed" : "pointer",
                            }}
                          >
                            {option.label}
                          </h6>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="d-flex justify-content-end gap-5 py-16 pe-16">
                <button
                  onClick={clearFields}
                  disabled={saving}
                  style={getButtonStyle(false)}
                >
                  {saving ? (
                    <>
                      {renderLoadingSpinner("sm")}
                      Clearing...
                    </>
                  ) : (
                    "Clear"
                  )}
                </button>
                <button
                  onClick={() => {
                    handleSave();
                    setShowRoleModal(false);
                  }}
                  disabled={saving}
                  style={getButtonStyle(true)}
                >
                  {saving ? (
                    <>
                      {renderLoadingSpinner("sm")}
                      {isEditMode ? "Updating..." : "Adding..."}
                    </>
                  ) : isEditMode ? (
                    "Update"
                  ) : (
                    "Add"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <>
        <AdminSideBar
          menuContent={menuContent}
          isMinimized={isSidebarMinimized}
        />
        <AdminTopNavBar
          toggleSidebar={toggleSidebar}
          isSidebarMinimized={isSidebarMinimized}
          homeLink={homeLink}
          title={
            isViewMode ? "VIEW ROLE" : isEditMode ? "EDIT ROLE" : "ADD ROLE"
          }
        />
        <div
          className="content-wrapper p-0"
          style={{
            marginLeft: isSidebarMinimized ? "80px" : "250px",
            flex: "1",
          }}
        >
          <section
            style={{
              backgroundColor: "#F1F4F9",
              padding: "30px",
              marginTop: "70px",
              marginBottom: "40px",
            }}
          >
            <div className="container-fluid">
              <div className="row">
                <div className="card border-0">
                  {isViewMode && (
                    <div className="card-body">
                      <div className="row mb-4">
                        <div className="col-md-6 mb-3">
                          <h6 className="text-md fw-bold">Role Name</h6>
                          <input
                            type="text"
                            className="form-control"
                            value={roleName}
                            readOnly
                            style={{
                              ...inputStyle,
                              backgroundColor: "#fff",
                              color: "#666",
                            }}
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <h6 className="text-md fw-bold">Status</h6>
                          <div className="pt-1">
                            <span
                              className="text-md fw-bold"
                              style={{
                                color: status ? "green" : "orange",
                              }}
                            >
                              {status ? "Active" : "Inactive"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {!isViewMode && (
                    <div className="d-flex justify-content-between align-items-center p-16">
                      <h6 className="mb-0 fs-5">Manage Permissions</h6>
                      <div className="d-flex gap-5">
                        <button
                          onClick={clearFieldsPermission}
                          disabled={saving}
                          style={getButtonStyle(false)}
                        >
                          {saving ? (
                            <>
                              {renderLoadingSpinner("sm")}
                              Clearing...
                            </>
                          ) : (
                            "Clear"
                          )}
                        </button>
                        <button
                          onClick={() => !saving && setShowRoleModal(true)}
                          disabled={saving}
                          style={getButtonStyle(true)}
                        >
                          {saving ? (
                            <>
                              {renderLoadingSpinner("sm")}
                              Processing...
                            </>
                          ) : isEditMode ? (
                            "Edit Role"
                          ) : (
                            "Add Role"
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="card-body">
                    {!isViewMode && (
                      <div className="row mb-4">
                        <div className="col-md-6 mb-32">
                          <h6 className="text-md mb-16 fw-bold">Roles</h6>
                          <select
                            className="form-select"
                            value={selectedRoleForPermissions}
                            onChange={(e) => {
                              if (saving) return;
                              const val = e.target.value;
                              setSelectedRoleForPermissions(val);
                              loadPermissionsForRole(
                                val ? parseInt(val, 10) : null,
                              );
                            }}
                            disabled={saving || isEditMode}
                            style={{
                              ...inputStyle,
                              borderColor: "#cbd5e1",
                              backgroundColor:
                                isEditMode || saving ? "#f8f9fa" : "#ffffff",
                              color:
                                isEditMode || saving ? "#6c757d" : "inherit",
                              cursor:
                                isEditMode || saving
                                  ? "not-allowed"
                                  : "pointer",
                            }}
                          >
                            <option value="">Select role</option>
                            {availableRoles
                              .filter((role) => {
                                if (!isEditMode && !isViewMode) {
                                  return !rolesWithPermissions.has(role.id);
                                }
                                return true;
                              })
                              .map((role) => (
                                <option key={role.id} value={String(role.id)}>
                                  {role.roleName}
                                </option>
                              ))}
                          </select>
                        </div>
                      </div>
                    )}

                    <div
                      className="border rounded"
                      style={{
                        backgroundColor: "#ffffff",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-around",
                          alignItems: "center",
                          ...permissionTableHeaderStyle,
                        }}
                      >
                        <span>Access to</span>
                        <span>Actions</span>
                      </div>
                      <div>
                        {Object.keys(permissions).map((module) => (
                          <div key={module} style={permissionTableRowStyle}>
                            <div
                              style={{
                                flex: 1,
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={
                                  permissions[module].add ||
                                  permissions[module].view ||
                                  permissions[module].edit ||
                                  permissions[module].delete
                                }
                                onChange={() => {
                                  if (isViewMode || saving) return;
                                  const newStatus = !(
                                    permissions[module].add ||
                                    permissions[module].view ||
                                    permissions[module].edit ||
                                    permissions[module].delete
                                  );
                                  setPermissions((prev) => ({
                                    ...prev,
                                    [module]: {
                                      add: newStatus,
                                      view: newStatus,
                                      edit: newStatus,
                                      delete: newStatus,
                                    },
                                  }));
                                }}
                                disabled={isViewMode || saving}
                                style={{
                                  width: "16px",
                                  height: "16px",
                                  accentColor: "#3b82f6",
                                  cursor:
                                    isViewMode || saving
                                      ? "not-allowed"
                                      : "pointer",
                                  opacity: isViewMode || saving ? 0.6 : 1,
                                }}
                              />
                              <h6
                                className="text-md fw-bold mb-0 ms-8"
                                style={{
                                  opacity: isViewMode || saving ? 0.7 : 1,
                                  cursor:
                                    isViewMode || saving
                                      ? "not-allowed"
                                      : "default",
                                }}
                              >
                                {module}
                              </h6>
                            </div>
                            <div style={{ display: "flex", gap: "24px" }}>
                              {["add", "view", "edit", "delete"].map(
                                (action) => (
                                  <div
                                    key={action}
                                    style={permissionCheckboxContainerStyle}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={permissions[module][action]}
                                      onChange={() =>
                                        handlePermissionChange(module, action)
                                      }
                                      disabled={isViewMode || saving}
                                      style={{
                                        width: "16px",
                                        height: "16px",
                                        accentColor: "#3b82f6",
                                        cursor:
                                          isViewMode || saving
                                            ? "not-allowed"
                                            : "pointer",
                                        opacity: isViewMode || saving ? 0.6 : 1,
                                      }}
                                    />
                                    <h6
                                      className="text-md fw-bold mb-0 ms-8"
                                      style={{
                                        opacity: isViewMode || saving ? 0.7 : 1,
                                        cursor:
                                          isViewMode || saving
                                            ? "not-allowed"
                                            : "default",
                                      }}
                                    >
                                      {action.charAt(0).toUpperCase() +
                                        action.slice(1)}
                                    </h6>
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {!isViewMode && (
                      <div className="d-flex justify-content-end gap-8 mt-16">
                        <button
                          onClick={handleSavePermissions}
                          disabled={saving || !selectedRoleForPermissions}
                          style={getButtonStyle(true)}
                        >
                          {saving ? (
                            <>
                              {renderLoadingSpinner("sm")}
                              Saving...
                            </>
                          ) : (
                            "Save Permissions"
                          )}
                        </button>
                        <button
                          onClick={resetPermissions}
                          disabled={saving}
                          style={getButtonStyle(false)}
                        >
                          {saving ? (
                            <>
                              {renderLoadingSpinner("sm")}
                              Resetting...
                            </>
                          ) : (
                            "Reset"
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
          <AdminFooter isSidebarMinimized={isSidebarMinimized} />
        </div>
      </>
    </>
  );
};;

export default AddRole;
